import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap } from 'rxjs';
import { CollectionCasePageDto, CollectionCaseService } from '../collection-case.service';
import {
  CollectionCaseSearchCriteria,
  CollectionCaseDetailDto,
  CollectionActionCommand,
} from '../models/collection-case.model';
import { MockDataLoader } from './mock-data-loader.service';
import { PaymentMockStateService } from './payment-mock-state.service';
import { ActionMockStateService } from './action-mock-state.service';
import { MockAccessScopeService } from './mock-access-scope.service';

interface CasesFixture { total: number; items: CollectionCaseDetailDto[] }
type CaseDetailsFixture = Record<string, CollectionCaseDetailDto>;

const NEXT_ACTION_BY_STATUS: Record<string, string> = {
  OPEN:      'Appel de relance prévu',
  PENDING:   'Suivi de promesse',
  SUSPENDED: 'Reprise à planifier',
  CLOSED:    '—',
};

// (AN-UX-24) — simulates server-side state mutation so recorded actions are
// visibly reflected on the Dossiers grid / case detail without a real backend.
interface CaseOverride {
  lastActionLabel?: string;
  categoryCode?: string;
  categoryLabel?: string;
  status?: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  FIRST_CONTACT:   'Premier Contact',
  STANDARD:        'Relance Standard',
  PROMISE_ACTIVE:  'Promesse en Cours',
  PROMISE_BROKEN:  'Promesse Rompue',
  URGENT:          'Action Urgente',
  PRE_LEGAL:       'Pré-Contentieux',
  LEGAL:           'Contentieux',
  SUSPENDED:       'Suspendu / Inactif',
};

const ACTION_TYPE_LABELS: Record<string, string> = {
  PHONE_CALL:     'Appel téléphonique',
  SMS:            'SMS',
  EMAIL:          'E-mail',
  LETTER:         'Courrier',
  VISIT:          'Visite',
  RELANCE:        'Relance',
  CONTACT_SEARCH: 'Recherche contact',
  CLOSURE:        'Clôture du dossier',
  action:         'Action enregistrée',
  promesse:       'Promesse de paiement',
  echeancier:     'Échéancier créé',
  escalade:       'Dossier escaladé',
  cloture:        'Clôture du dossier',
};

@Injectable()
export class CollectionCaseMockService extends CollectionCaseService {
  private readonly mockData = inject(MockDataLoader);
  private readonly paymentState = inject(PaymentMockStateService);
  private readonly actionState = inject(ActionMockStateService);
  private readonly accessScope = inject(MockAccessScopeService);
  private readonly overrides = new Map<string, CaseOverride>();

  getCases(criteria: CollectionCaseSearchCriteria): Observable<CollectionCasePageDto> {
    return this.mockData.load<CasesFixture>('collection-cases').pipe(
      map(fixture => {
        let items: any[] = this.accessScope.filterAuthorizedCases(fixture.items).map(c => {
          const override = this.overrides.get(c.caseId);
          const promiseActive = this.paymentState.hasActivePromise(c.caseId);
          const paymentPlan = this.paymentState.getPaymentPlan(c.caseId);
          const lastAction = this.actionState.getLastForCase(c.caseId);
          return {
            ...c,
            ...(promiseActive ? { categoryCode: 'PROMISE_ACTIVE', categoryLabel: CATEGORY_LABELS['PROMISE_ACTIVE'] } : {}),
            ...(paymentPlan ? { hasActivePaymentPlan: paymentPlan.status === 'ACTIVE' } : {}),
            ...(override ?? {}),
            ...(lastAction ? { lastActionLabel: this.actionState.toInteraction(lastAction).actionLabel, lastActionDate: lastAction.executionDate ?? lastAction.plannedDate } : {}),
          };
        });

        if (criteria.agentId)           items = items.filter(c => c.agentId === criteria.agentId);
        if (criteria.status)            items = items.filter(c => c.status === criteria.status);
        if (criteria.phase)             items = items.filter(c => c.phase === criteria.phase);
        if (criteria.categoryCode)      items = items.filter(c => c.categoryCode === criteria.categoryCode);
        if (criteria.priority)          items = items.filter(c => c.priority === criteria.priority);
        if (criteria.debtorName)        items = items.filter(c => c.debtorName?.toLowerCase().includes(criteria.debtorName!.toLowerCase()));
        if (criteria.caseReference)     items = items.filter(c => c.caseReference?.toLowerCase().includes(criteria.caseReference!.toLowerCase()));
        if (criteria.amountMin  != null) items = items.filter(c => c.overdueAmount >= criteria.amountMin!);
        if (criteria.amountMax  != null) items = items.filter(c => c.overdueAmount <= criteria.amountMax!);
        if (criteria.daysLateMin != null) items = items.filter(c => c.daysLate >= criteria.daysLateMin!);
        if (criteria.daysLateMax != null) items = items.filter(c => c.daysLate <= criteria.daysLateMax!);

        if (criteria.sort) {
          const [col, dir] = criteria.sort.split(',');
          items = [...items].sort((a, b) => {
            const av = a[col], bv = b[col];
            if (av === bv) return 0;
            const cmp = av < bv ? -1 : 1;
            return dir === 'asc' ? cmp : -cmp;
          });
        }

        items = items.map(c => ({
          ...c,
          nextActionLabel: c.nextActionLabel ?? NEXT_ACTION_BY_STATUS[c.status] ?? '—',
        }));

        const total = items.length;
        const start = criteria.page * criteria.size;
        return { total, items: items.slice(start, start + criteria.size) } as CollectionCasePageDto;
      }),
    );
  }

  // AN-UX-78: hand-authored fixtures only exist for a handful of representative
  // caseIds (case-details.json); every other caseId present in collection-cases.json
  // falls back to a detail synthesized from its list row rather than reusing
  // another case's data.
  getCaseDetail(caseId: string): Observable<CollectionCaseDetailDto> {
    return this.mockData.load<CaseDetailsFixture>('case-details').pipe(
      switchMap(details => {
        const detail = details[caseId];
        if (detail) {
          if (!this.accessScope.canAccessCase(detail)) return of(null as unknown as CollectionCaseDetailDto);
          return of(detail);
        }
        return this.mockData.load<CasesFixture>('collection-cases').pipe(
          map(fixture => {
            const listItem = fixture.items.find((c: any) => c.caseId === caseId);
            if (!listItem || !this.accessScope.canAccessCase(listItem)) {
              throw { status: 403, message: 'Dossier inaccessible pour le profil mock actif.' };
            }
            return this.synthesizeDetail(listItem);
          }),
        );
      }),
      map(detail => {
        if (!detail) throw { status: 403, message: 'Dossier inaccessible pour le profil mock actif.' };
        const override = this.overrides.get(detail.caseId);
        const promiseActive = this.paymentState.hasActivePromise(detail.caseId);
        const actions = this.actionState.getForCase(detail.caseId);
        const paymentPlan = this.paymentState.getPaymentPlan(detail.caseId);
        const planHistory = this.paymentState.getPlanHistory(detail.caseId);
        const lastAction = this.actionState.getLastForCase(detail.caseId);
        if (!override && !promiseActive && actions.length === 0 && !paymentPlan) return detail;
        return {
          ...detail,
          ...(promiseActive ? { hasActivePromise: true, categoryCode: 'PROMISE_ACTIVE', categoryLabel: CATEGORY_LABELS['PROMISE_ACTIVE'] } : {}),
          categoryCode:  override?.categoryCode  ?? (promiseActive ? 'PROMISE_ACTIVE' : detail.categoryCode),
          categoryLabel: override?.categoryLabel ?? (promiseActive ? CATEGORY_LABELS['PROMISE_ACTIVE'] : detail.categoryLabel),
          status:        override?.status        ?? detail.status,
          hasActivePaymentPlan: paymentPlan?.status === 'ACTIVE' || detail.hasActivePaymentPlan,
          paymentPlan: paymentPlan ? {
            totalAmount: paymentPlan.totalAmount,
            installmentCount: paymentPlan.installmentCount,
            frequency: paymentPlan.frequency,
            firstInstallmentDate: paymentPlan.firstInstallmentDate,
            derogation: paymentPlan.derogation,
            derogationReason: paymentPlan.derogationReason,
            installments: paymentPlan.installments,
          } : detail.paymentPlan,
          lastActionDate: lastAction?.executionDate ?? lastAction?.plannedDate ?? detail.lastActionDate,
          interactions: [
            ...actions.map(a => this.actionState.toInteraction(a)),
            ...(detail.interactions ?? []),
          ],
          history: [
            ...actions.map(a => this.actionState.toHistory(a)),
            ...planHistory,
            ...(detail.history ?? []),
          ],
        };
      }),
    );
  }

  private synthesizeDetail(listItem: any): CollectionCaseDetailDto {
    return {
      caseId:          listItem.caseId,
      caseReference:   listItem.caseReference,
      debtorId:        listItem.debtorId,
      debtorName:      listItem.debtorName,
      creditorId:      listItem.creditorId,
      creditorLabel:   listItem.creditorLabel,
      phase:           listItem.phase,
      status:          listItem.status,
      priority:        listItem.priority,
      categoryCode:    listItem.categoryCode,
      categoryLabel:   listItem.categoryLabel,
      overdueAmount:   listItem.overdueAmount,
      totalDebtAmount: listItem.totalDebtAmount,
      daysLate:        listItem.daysLate,
      openDate:        listItem.lastActionDate,
      lastActionDate:  listItem.lastActionDate,
      agentId:         listItem.agentId,
      agentName:       listItem.agentName,
      contractReference: listItem.contractReference,
      hasActivePromise:     listItem.categoryCode === 'PROMISE_ACTIVE',
      hasActivePaymentPlan: false,
      nextActionLabel: listItem.nextActionLabel ?? NEXT_ACTION_BY_STATUS[listItem.status] ?? '—',
      dmnDecision: {
        segment: 'STANDARD',
        nextBestAction: 'PHONE_CALL',
        nextBestActionReason: 'Relance standard recommandée',
        score: 50,
        computedAt: listItem.lastActionDate,
      },
      debts: [{
        debtId: `${listItem.caseId}-D1`,
        contractReference: listItem.contractReference,
        amount: listItem.overdueAmount,
        dueDate: listItem.lastActionDate,
        daysLate: listItem.daysLate,
        status: 'OVERDUE',
      }],
      interactions: [],
      documents: [],
      history: [],
    };
  }

  performAction(command: CollectionActionCommand): Observable<void> {
    return this.mockData.load<CasesFixture>('collection-cases').pipe(
      map(fixture => {
        const previous = this.overrides.get(command.caseId);
        const current = fixture.items.find((c: any) => c.caseId === command.caseId) as any;
        const currentCategory = previous?.categoryCode ?? current?.categoryCode;

        // Category transition rules (PRD Annexe A) — a case moves out of
        // its bucket only when the action's outcome justifies it.
        let nextCategory = currentCategory;
        if (command.createPromise) {
          // A new payment commitment supersedes an unhonored one: exits PROMISE_BROKEN.
          nextCategory = 'PROMISE_ACTIVE';
        } else if (command.outcome === 'REFUSAL') {
          nextCategory = 'URGENT';
        } else if (
          (command.outcome === 'CONTACT_SUCCESS' || command.outcome === 'INFO_GATHERED') &&
          currentCategory === 'FIRST_CONTACT'
        ) {
          nextCategory = 'STANDARD';
        }

        const override: CaseOverride = {
          ...previous,
          lastActionLabel: ACTION_TYPE_LABELS[command.actionType] ?? command.actionType,
        };
        if (nextCategory && nextCategory !== currentCategory) {
          override.categoryCode  = nextCategory;
          override.categoryLabel = CATEGORY_LABELS[nextCategory] ?? nextCategory;
        }
        if (command.actionType === 'CLOSURE') {
          override.status = 'CLOSED';
        }
        this.overrides.set(command.caseId, override);
        this.actionState.add(command, {
          debtorId: current?.debtorId,
          createdBy: current?.agentName,
        });
        return undefined;
      }),
    );
  }
}
